import { Subject } from 'rxjs';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { ConsentType } from '@core/services/cookie-consent/cookie-consent.config';
import { CookieConsentService } from '@core/services/cookie-consent/cookie-consent.service';
import { IntersectionService } from '@core/services/intersection.service';
import { BreakpointService } from '@libs/breakpoints';
import { UmbracoImage } from '@models/umbraco.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { fadeTransition } from '@shared/animations/animations';
import { VideoPlayerService } from '@features/video-player/video-player.service';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { FormatStringPipe } from '@shared/format-string/format-string.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { SafePipe } from '@shared/safe/safe.pipe';
import { ImageComponent } from '@shared/image/image/image.component';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { NgClass, NgIf } from '@angular/common';

@UntilDestroy()
@Component({
    selector: 'app-youtube-player',
    templateUrl: './youtube-player.component.html',
    styleUrls: ['../video-player.shared.scss', './youtube-player.component.scss'],
    animations: [fadeTransition],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        NgIf,
        SvgIconComponent,
        ImageComponent,
        SafePipe,
        TranslateModule,
        FormatStringPipe,
        TranslateSafePipe
    ],
    providers: [VideoPlayerService]
})
export class YoutubePlayerComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly unsubscribe: Subject<void> = new Subject();

    private intersectionKey: string = 'youtube-video-player';
    private intersectionOptions: IntersectionObserverInit = {
        threshold: 0.5
    };
    private isIntersecting = false;
    private playerIsReady = false;

    public showVideoFrame = false;
    public loadIFrame = false;
    public player?: YT.Player;
    public videoError?: string;
    public isSmallDevice: boolean = false;
    public showConsentDisclaimer: boolean = false;

    @Input() public videoId?: string;
    @Input() public thumbnail: UmbracoImage;
    @Input() public caption?: string;
    @Input() public autoplay = false;
    @Input() public loop = false;
    @Input() public controls = true;
    @Input() public muted = false;

    @ViewChild('playerEl')
    public playerEl?: ElementRef<HTMLDivElement>;

    @ViewChild('videoEl')
    public videoEl?: ElementRef<HTMLDivElement>;

    @ViewChild('videoWrapperEl')
    public videoWrapperEl?: ElementRef<HTMLDivElement>;

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly element: ElementRef<HTMLElement>,
        private readonly intersectionService: IntersectionService,
        private readonly breakpointService: BreakpointService,
        public readonly videoPlayerService: VideoPlayerService,
        public readonly cookieConsentService: CookieConsentService
    ) {}

    ngOnInit(): void {
        this.breakpointService.breakpoint$.pipe(takeUntil(this.unsubscribe)).subscribe(breakpoints => {
            this.isSmallDevice = breakpoints.some(breakpoint => breakpoint.name.includes('mobile'));
            this.checkVideoThumbnail(this.isSmallDevice);
            this.cd.detectChanges();
        });
    }

    ngAfterViewInit(): void {
        const isBrowser = this.breakpointService.isBrowser();
        if (!isBrowser && !this.playerEl) {
            return;
        }

        this.cookieConsentService
            .addScriptToLoadIfConsentGiven({
                url: 'https://www.youtube.com/iframe_api',
                type: ConsentType.Statistic
            })
            .then((status: boolean) => {
                this.showConsentDisclaimer = false;

                if (!status) {
                    return;
                }

                this.cd.detectChanges();

                this.intersectionService
                    .observe(this.intersectionKey, this.element.nativeElement, this.intersectionOptions)
                    .pipe(
                        // i added a debounceTime because if we scroll right to the end of the page
                        // will trigger the video right away if we have autoplay on;
                        debounceTime(100),
                        takeUntil(this.unsubscribe),
                        filter((entry: IntersectionObserverEntry) => entry.target === this.element.nativeElement)
                    )
                    .subscribe(entry => {
                        this.intersectionControlPlayer(entry);
                    });
            })
            .catch(reason => (this.videoError = reason.message));
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();

        if (this.player) {
            this.player.destroy();
            this.playerIsReady = false;
        }
    }

    public loadVideoFrame(): void {
        this.cookieConsentService.consentStatus$.pipe(take(1), untilDestroyed(this)).subscribe(status => {
            if (status.cookie_cat_statistic) {
                this.loadIFrame = true;
                this.setPlayer();
            } else {
                this.showConsentDisclaimer = true;
            }
            this.cd.markForCheck();
        });
    }

    private checkVideoThumbnail(isSmallDevice: boolean = false): void {
        const { videoId, thumbnail } = this;

        if (!thumbnail && videoId) {
            this.thumbnail = {
                src: `https://img.youtube.com/vi/${this.videoPlayerService.getYoutubeVideoId(videoId)}/${
                    isSmallDevice ? 'hqdefault.jpg' : 'maxresdefault.jpg'
                }`,
                width: isSmallDevice ? 480 : 1024,
                height: isSmallDevice ? 360 : 619
            };
        }
    }

    private setPlayer(): void {
        const { videoId, videoEl } = this;
        if (videoId && videoEl?.nativeElement) {
            const { loop, controls } = this;

            const playerVars: YT.PlayerVars = {
                enablejsapi: 1,
                autoplay: 1,
                controls: +controls,
                loop: +loop,
                showinfo: 0, // Hide the video title
                modestbranding: 0, // Hide the Youtube Logo
                cc_load_policy: 1
            };

             
            this.player = new YT.Player(videoEl?.nativeElement, {
                videoId: this.videoPlayerService.getYoutubeVideoId(videoId),
                playerVars: playerVars,
                events: {
                    onError: this.onPlayerError.bind(this),
                    onReady: this.onPlayerReady.bind(this)
                }
            });
        }
    }

    private onPlayerReady(): void {
        this.showVideoFrame = true;
        if (!this.player) {
            return;
        }
        if (this.muted) {
            this.player.mute();
        }

        this.player.playVideo();

        // if the when the player is ready the element is not in the viewport
        // will pause the video right away
        if (!this.isIntersecting) {
            this.player.pauseVideo();
        }

        this.playerIsReady = true;
        this.cd.markForCheck();
    }

    private intersectionControlPlayer(entry: IntersectionObserverEntry): void {
        this.isIntersecting = entry.isIntersecting;
        if (this.player && this.playerIsReady) {
            if (
                !entry.isIntersecting &&
                (this.player.getPlayerState() === YT.PlayerState.PLAYING ||
                    this.player.getPlayerState() === YT.PlayerState.BUFFERING)
            ) {
                this.player.pauseVideo();
                return;
            }

            if (entry.isIntersecting && this.autoplay) {
                this.player.playVideo();
                return;
            }
        }

        if (entry.isIntersecting && this.autoplay) {
            this.loadVideoFrame();
        }
    }

    private resetVideoPlayer(): void {
        const { videoWrapperEl, playerEl, player } = this;
        if (videoWrapperEl?.nativeElement && playerEl?.nativeElement) {
            videoWrapperEl.nativeElement.style.paddingTop = '0';
        }

        this.showVideoFrame = false;
        this.loadIFrame = true;
        if (player) {
            player.destroy();
        }
    }

    private onPlayerError(error: YT.OnErrorEvent): void {
        this.resetVideoPlayer();

        switch (error.data) {
            case YT.PlayerError.InvalidParam:
                this.videoError = 'Video ID does not have 11 characters, or the video ID contains invalid characters';
                break;

            case YT.PlayerError.Html5Error:
                this.videoError =
                    'The requested content cannot be played in an HTML5 player or another error related to the HTML5 player has occurred.';
                break;

            case YT.PlayerError.VideoNotFound:
                this.videoError = 'The video requested was not found';
                break;

            case YT.PlayerError.EmbeddingNotAllowed:
            case YT.PlayerError.EmbeddingNotAllowed2:
                this.videoError = 'The owner of the requested video does not allow it to be played in embedded players';
                break;

            default:
                break;
        }

        this.cd.markForCheck();
    }
}
